import React from 'react';
import { motion } from 'framer-motion';
import { Sparkles } from 'lucide-react';

// Explosion burst particles
const BurstParticles = () => {
  const particles = Array.from({ length: 40 }, (_, i) => {
    const angle = (i / 40) * 360;
    const distance = 200 + Math.random() * 400;
    const size = Math.random() * 4 + 2;
    const duration = 3 + Math.random() * 4;
    
    return {
      id: i,
      angle,
      distance,
      size,
      duration,
      delay: Math.random() * 0.5,
    };
  });

  return (
    <div className="burst-container">
      {particles.map((particle) => (
        <motion.div
          key={particle.id}
          className="burst-particle"
          style={{
            width: particle.size,
            height: particle.size,
          }}
          initial={{ 
            x: 0, 
            y: 0, 
            opacity: 0,
            scale: 0
          }}
          animate={{ 
            x: Math.cos(particle.angle * Math.PI / 180) * particle.distance,
            y: Math.sin(particle.angle * Math.PI / 180) * particle.distance,
            opacity: [0, 1, 1, 0],
            scale: [0, 1, 1, 0.5]
          }}
          transition={{
            duration: particle.duration,
            delay: particle.delay,
            repeat: Infinity,
            repeatDelay: 2,
            ease: 'easeOut',
          }}
        />
      ))}
    </div>
  );
};

// Radial light rays
const LightRays = () => {
  const rays = Array.from({ length: 12 }, (_, i) => ({
    id: i,
    rotation: (i / 12) * 360,
    delay: i * 0.1,
  }));

  return (
    <div className="light-rays-container">
      {rays.map((ray) => (
        <motion.div
          key={ray.id}
          className="light-ray"
          style={{
            transform: `rotate(${ray.rotation}deg)`,
          }}
          initial={{ opacity: 0, scaleY: 0 }}
          animate={{ 
            opacity: [0, 0.3, 0],
            scaleY: [0, 1, 0]
          }}
          transition={{
            duration: 4,
            delay: ray.delay,
            repeat: Infinity,
            repeatDelay: 1,
            ease: 'easeInOut',
          }}
        />
      ))}
    </div>
  );
};

// Floating sparkles
const FloatingSparkles = () => {
  const sparkles = Array.from({ length: 20 }, (_, i) => ({
    id: i,
    left: Math.random() * 100,
    top: Math.random() * 100,
    size: Math.random() * 6 + 2,
    delay: Math.random() * 3,
    duration: 2 + Math.random() * 2,
  }));

  return (
    <div className="sparkles-container">
      {sparkles.map((sparkle) => (
        <motion.div
          key={sparkle.id}
          className="sparkle-dot"
          style={{
            left: `${sparkle.left}%`,
            top: `${sparkle.top}%`,
            width: sparkle.size,
            height: sparkle.size,
          }}
          animate={{
            opacity: [0, 1, 0],
            scale: [0.5, 1.2, 0.5],
          }}
          transition={{
            duration: sparkle.duration,
            delay: sparkle.delay,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
      ))}
    </div>
  );
};

const NewHeroSection = () => {

  return (
    <section className="new-hero-section">
      {/* Animated background effects */}
      <div className="hero-background">
        {/* Burst particles from center */}
        <BurstParticles />
        
        {/* Floating sparkles */}
        <FloatingSparkles />
        
        {/* Ambient orbs */}
        <motion.div 
          className="gold-orb orb-1"
          animate={{ 
            y: [0, -30, 0],
            opacity: [0.3, 0.6, 0.3]
          }}
          transition={{ 
            duration: 6,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div 
          className="gold-orb orb-2"
          animate={{ 
            y: [0, 30, 0],
            opacity: [0.2, 0.5, 0.2]
          }}
          transition={{ 
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1
          }}
        />
        
        {/* Bokeh glow effects */}
        <div className="hero-bokeh bokeh-1"></div>
        <div className="hero-bokeh bokeh-2"></div>
        <div className="hero-bokeh bokeh-3"></div>
      </div>

      <div className="hero-content-wrapper">
        {/* Main Hero Content */}
        <motion.div
          className="hero-main-content"
        >
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ 
              opacity: 1, 
              y: 0,
              scale: [1, 1.02, 1],
            }}
            transition={{ 
              duration: 1, 
              delay: 0.5,
              scale: {
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut"
              }
            }}
            className="hero-badge"
          >
            <Sparkles className="sparkle-icon" size={16} />
            <span>Golden Jubilee 1976–2026</span>
          </motion.div>

          {/* Organization Name — sits above the logo */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.65 }}
            className="hero-org-name"
          >
            International Precious Metals Institute
          </motion.p>

          {/* Large 50 Anniversary Logo — the visual centerpiece */}
          <motion.img
            src="/ipmi-50th-logo.svg"
            alt="IPMI 50th Anniversary"
            className="hero-anniversary-logo"
            initial={{ opacity: 0, scale: 0.7, rotate: -3 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ duration: 1.3, delay: 0.8, ease: [0.22, 1, 0.36, 1] }}
          />

          {/* "A Double Feature" eyebrow */}
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.0 }}
            className="hero-films-eyebrow"
          >
            <span className="hero-films-eyebrow-dash" />
            A Double Feature
            <span className="hero-films-eyebrow-dash" />
          </motion.span>

          {/* Main Headline — The Story in Motion */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.1 }}
            className="hero-main-title"
          >
            The Story <em className="gold-italic">in Motion</em>
          </motion.h1>

          {/* Subtitle — Celebrating 50 Years (demoted from H1) */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.25 }}
            className="hero-celebrating-subtitle"
          >
            Celebrating <span className="gold-shimmer-inline">50 Years</span> · 1976–2026
          </motion.p>

        </motion.div>
      </div>
    </section>
  );
};

export default NewHeroSection;
